import type { ShopperProducts } from '@/scapi';
import { isNotifyMeEnabled } from '@/features/notify-me/lib/notify-me-eligibility';

type Product = ShopperProducts.schemas['Product'];

export type ProductCardBadge = {
    name?: string;
    copy?: string;
    callout?: string;
    color?: string | null;
    background?: string | null;
    isShowBadgeOnPDP?: boolean;
};

/**
 * Subset of `c_quickBuyAttributes` shape we depend on. The full object holds richer
 * pricing/CTA metadata; we only care about the colour swatch + per-value orderability.
 */
type QuickBuyAttribute = {
    id: string;
    isColor?: boolean;
    isSize?: boolean;
    type?: string;
    displayName?: string;
    values?: Array<{
        id?: string;
        value?: string;
        displayValue?: string;
        isOrderable?: boolean;
        selectable?: boolean;
        selected?: boolean;
        unavailable?: boolean;
        notifyMe?: boolean;
        swatch?: { colour?: string | null };
    }>;
};

type ProductCustomFields = {
    c_badges?: {
        promotionalBadges?: { GWP?: ProductCardBadge[]; offers?: ProductCardBadge[] };
        tradeBadges?: ProductCardBadge[];
        miscBadges?: ProductCardBadge[];
    };
    c_quickBuyAttributes?: Record<string, QuickBuyAttribute>;
};

const asCustom = (product: Product): ProductCustomFields => product as unknown as ProductCustomFields;

/**
 * Returns `s` when it's a non-empty, non-blank string; otherwise `undefined`.
 * Used in label resolution so that `name: ""` falls through to the next source
 * rather than producing an inaccessible empty label for swatches/chips.
 */
const nonEmpty = (s: string | null | undefined): string | undefined =>
    s != null && s.trim().length > 0 ? s : undefined;

/**
 * Promotional badges we surfaces on PLP tiles. Pulls GWP + offers from `c_badges`,
 * keeping only entries with displayable copy.
 */
export const getProductCardBadges = (product: Product): ProductCardBadge[] => {
    const promo = asCustom(product).c_badges?.promotionalBadges;
    return [...(promo?.GWP ?? []), ...(promo?.offers ?? [])].filter(
        (b) => b.isShowBadgeOnPDP !== false && Boolean(b.callout || b.copy)
    );
};

export type ProductCardActionKind = 'add-to-bag' | 'notify-me' | 'out-of-stock' | 'select-shade' | 'select-size';

/**
 * Resolved CTA state. `kind` is the stable analytics identifier (locale-invariant). The display
 * label is *not* on this payload by design — it would leak the English string into every locale's
 * analytics event. Consumers should branch off `kind` and resolve the label via i18n at the
 * render site.
 */
export type ProductCardActionState = {
    kind: ProductCardActionKind;
    variant: 'primary' | 'secondary';
    isOrderable: boolean;
    allowsNotify: boolean;
    disabled: boolean;
};

/**
 * Static variant / disabled config for each kind. Sourced from Figma examples frame `549:1869`.
 * Selecting a shade or size never flips the kind — once resolved, the CTA appearance is fixed.
 */
const KIND_PRESETS: Record<ProductCardActionKind, { variant: 'primary' | 'secondary'; disabled: boolean }> = {
    'add-to-bag': { variant: 'primary', disabled: false },
    'notify-me': { variant: 'secondary', disabled: false },
    'out-of-stock': { variant: 'primary', disabled: true },
    'select-shade': { variant: 'secondary', disabled: false },
    'select-size': { variant: 'secondary', disabled: false },
};

type GetProductCardActionStateOptions = {
    /**
     * Force a specific action kind. When set, the resolved state uses that kind's preset directly
     * (e.g. `kind: 'select-shade'` always renders the Select Shade button regardless of inventory).
     * When omitted, the kind is derived from availability + the `c_isNotifyme` flag.
     */
    kind?: ProductCardActionKind;
};

/**
 * Resolves the CTA state. When `kind` is supplied the preset for that kind is returned as-is.
 * Otherwise the kind is derived from inventory + the `c_isNotifyme` flag:
 * - orderable → `add-to-bag`
 * - !orderable + `c_isNotifyme` (and not `c_disableNotifyMe`) → `notify-me`
 * - otherwise → `out-of-stock`
 */
export const getProductCardActionState = (
    product: Product,
    { kind }: GetProductCardActionStateOptions = {}
): ProductCardActionState => {
    const isOrderable = product.inventory?.orderable !== false;
    const allowsNotify = isNotifyMeEnabled(product);
    const resolvedKind = kind ?? deriveAvailabilityKind(isOrderable, allowsNotify);
    return { kind: resolvedKind, ...KIND_PRESETS[resolvedKind], isOrderable, allowsNotify };
};

const deriveAvailabilityKind = (isOrderable: boolean, allowsNotify: boolean): ProductCardActionKind => {
    if (isOrderable) return 'add-to-bag';
    if (allowsNotify) return 'notify-me';
    return 'out-of-stock';
};

export type ProductCardAttribute =
    | {
          kind: 'colour';
          id: 'color' | 'shade';
          /** Value id pre-selected by the product — variant's own variationValues, or the
           *  c_quickBuyAttributes value marked `selected: true`. Undefined for masters. */
          initiallySelected?: string;
          values: { id: string; label: string; color: string; isOutOfStock: boolean }[];
      }
    | {
          kind: 'size';
          id: 'size';
          initiallySelected?: string;
          values: { id: string; label: string; isOutOfStock: boolean }[];
      };

const COLOUR_IDS = ['color', 'shade'] as const;

const getInitiallySelected = (
    product: Product,
    attrId: string,
    quickBuyAttr?: QuickBuyAttribute
): string | undefined => {
    const variationValues = (product as { variationValues?: Record<string, string> }).variationValues;
    if (variationValues?.[attrId]) return variationValues[attrId];
    const found = quickBuyAttr?.values?.find((v) => v.selected === true);
    // Use value ?? id for consistency with the quickBuyByValue map key — QuickBuy values
    // sometimes carry only `id` (no `value`), and we must resolve the same identifier.
    return found != null ? (found.value ?? found.id) : undefined;
};

/**
 * Resolves the single variation attribute our products carry.
 *
 * Catalog rule: a product has at most ONE variation attribute (colour/shade XOR size),
 * never both. Returns `null` when the product has no variation or only a single variant — both
 * cases mean there's nothing to pick from.
 */
export const getProductCardAttribute = (product: Product): ProductCardAttribute | null => {
    const attrs = product.variationAttributes ?? [];
    const variants = product.variants ?? [];
    const quickBuy = asCustom(product).c_quickBuyAttributes ?? {};

    const colourAttr = attrs.find((a) => COLOUR_IDS.includes(a.id as (typeof COLOUR_IDS)[number]));
    if (colourAttr && (colourAttr.values?.length ?? 0) > 1) {
        const orderableByColour = new Map<string, boolean>(
            variants.flatMap((variant) => {
                const variationValues = variant.variationValues as Record<string, string> | undefined;
                const value = variationValues?.[colourAttr.id];
                return value ? [[value, variant.orderable !== false] as const] : [];
            })
        );
        const quickBuyByValue = new Map(
            (quickBuy[colourAttr.id]?.values ?? []).flatMap((qv) => {
                const key = qv.value ?? qv.id;
                return key ? [[key, qv] as const] : [];
            })
        );
        return {
            kind: 'colour',
            id: colourAttr.id as 'color' | 'shade',
            initiallySelected: getInitiallySelected(product, colourAttr.id, quickBuy[colourAttr.id]),
            /* v8 ignore next -- colourAttr.values is guaranteed non-null: length > 1 checked above */
            values: (colourAttr.values ?? []).map((v) => {
                const raw = v as { value?: string; name?: string; orderable?: boolean; c_hex?: string };
                const id = raw.value ?? '';
                const variantOrderable = orderableByColour.get(id);
                const quickBuyValue = quickBuyByValue.get(id);
                return {
                    id,
                    label: nonEmpty(raw.name) ?? nonEmpty(quickBuyValue?.displayValue) ?? id,
                    color: quickBuyValue?.swatch?.colour ?? raw.c_hex ?? '#cccccc',
                    isOutOfStock:
                        variantOrderable === false ||
                        raw.orderable === false ||
                        quickBuyValue?.isOrderable === false ||
                        quickBuyValue?.unavailable === true,
                };
            }),
        };
    }

    const sizeAttr = attrs.find((a) => a.id === 'size');
    if (sizeAttr && (sizeAttr.values?.length ?? 0) > 1) {
        const orderableBySize = new Map<string, boolean>(
            variants.flatMap((variant) => {
                const variationValues = variant.variationValues as Record<string, string> | undefined;
                const value = variationValues?.size;
                return value ? [[value, variant.orderable !== false] as const] : [];
            })
        );
        return {
            kind: 'size',
            id: 'size',
            initiallySelected: getInitiallySelected(product, 'size', quickBuy.size),
            /* v8 ignore next -- sizeAttr.values is guaranteed non-null: length > 1 checked above */
            values: (sizeAttr.values ?? []).map((v) => {
                const raw = v as { value?: string; name?: string; orderable?: boolean };
                const id = raw.value ?? '';
                const variantOrderable = orderableBySize.get(id);
                return {
                    id,
                    label: nonEmpty(raw.name) ?? id,
                    isOutOfStock: variantOrderable === false || raw.orderable === false,
                };
            }),
        };
    }

    return null;
};
